<template>
  <v-card flat>
    <v-card-title class="primaryDialogTitle">
      <span class="headline">{{ isNew ? 'Add New Staff' : 'Details of Staff' }}</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <ValidationObserver ref="form" v-slot="{ invalid }" tag="form" autocomplete="off">
        <!-- Status -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-switch
              v-model="formData.enabled"
              :label="formData.enabled ? 'Active' : 'Inactive'"
              dense
              inset
              hide-details
            />
          </v-col>
        </v-row>

        <!-- Contact Number -->
        <v-row>
          <v-col cols="12">
            <fletrix-phone
              label="Mobile no.(Login Account)"
              ref="phoneValidator"
              v-model="formData.phone"
              :rules="{ required: true }"
            />
          </v-col>
        </v-row>

        <!-- Password Field (only for new staff) -->
        <v-row v-if="isNew">
          <v-col cols="12">
            <ValidationProvider v-slot="{ errors }" name="Password" rules="required|min:6">
              <fletrix-text-field
                v-model="formData.password"
                label="Password"
                :error="!!errors.length"
                :error-messages="errors"
                type="password"
                autocomplete="new-password"
                name="staff-new-password"
              />
            </ValidationProvider>
          </v-col>
        </v-row>
        <!-- Change Password Button (for existing staff) -->
        <v-row v-if="!isNew && isAbleToChangePassword">
          <v-col cols="12">
            <v-btn color="primary" @click="openChangePasswordDialog" outlined>
              Change Password
            </v-btn>
          </v-col>
        </v-row>

        <!-- Actions -->
        <v-row class="mt-4">
          <v-col cols="12" class="text-right">
            <v-btn text @click="$emit('close')" class="mr-2">Cancel</v-btn>
            <v-btn color="success" :loading="loading" :disabled="invalid" @click="save">
              {{ isNew ? 'Create' : 'Save' }}
            </v-btn>
          </v-col>
        </v-row>
      </ValidationObserver>
    </v-card-text>

    <!-- Change Password Dialog -->
    <v-dialog v-model="changePasswordDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">Change Password</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeChangePasswordDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <ValidationObserver ref="passwordForm" v-slot="{ invalid, valid }">
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :rules="{ required: true, regex: /^.{5,}$/ }"
                    name="new password"
                    vid="newPasswordConfirm"
                  >
                    <fletrix-text-field
                      v-model="passwordForm.newPw"
                      :error="!!errors.length"
                      :error-messages="errors.length ? 'Must be 5 characters or more' : ''"
                      label="New Password"
                      outlined
                      autocomplete="new-password"
                      :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showNewPassword ? 'text' : 'password'"
                      @click:append="showNewPassword = !showNewPassword"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="confirm new password"
                    rules="required|confirmed:newPasswordConfirm"
                  >
                    <fletrix-text-field
                      v-model="passwordForm.confirmNewPw"
                      :error="!!errors.length"
                      :error-messages="errors"
                      label="Confirm New Password"
                      outlined
                      autocomplete="new-password"
                      :append-icon="showConfirmNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showConfirmNewPassword ? 'text' : 'password'"
                      @click:append="showConfirmNewPassword = !showConfirmNewPassword"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeChangePasswordDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :disabled="!valid"
              :loading="changingPassword"
              @click="resetUserPassword"
            >
              Change Password
            </v-btn>
          </v-card-actions>
        </ValidationObserver>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from '@/api';
import { mapActions } from 'vuex';
import { FletrixTextField, FletrixPhone } from '@/components/Atom';
import { crudRead } from '@/api/crud';
import { mapGetters } from 'vuex';

export default {
  name: 'StaffCard',
  components: {
    FletrixTextField,
    FletrixPhone,
  },
  props: {
    staffId: {
      type: String,
      default: null,
    },
    clinicId: {
      type: String,
      required: true,
    },
    staffData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      formData: {
        enabled: true,
        phone: '',
        password: '',
      },
      changePasswordDialog: false,
      changingPassword: false,
      passwordForm: {
        newPw: null,
        confirmNewPw: null,
      },
      showNewPassword: false,
      showConfirmNewPassword: false,
      populatedStaffData: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'profile']),

    isNew() {
      return !this.staffId || this.staffId === 'new';
    },
    isMe() {
      return this.profile._id === this.staffId;
    },
    isAbleToChangePassword() {
      return this.isAdmin || this.isMe;
    },
  },
  watch: {
    staffData: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.isNew) {
          this.parseStaffData(newVal);
        }
      },
    },
  },
  mounted() {
    if (this.staffData && !this.isNew) {
      this.parseStaffData(this.staffData);
    }
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    parseStaffData(data) {
      if (!data) return;

      // Basic fields - convert status to enabled boolean
      this.formData.enabled = data.enabled !== undefined ? data.enabled : true;

      // Parse phone - PhoneValidator expects full phone with country code
      if (data.phone) {
        this.formData.phone = data.phone;
      }
    },

    async save() {
      const isValid =
        this.$refs.form && this.$refs.form.validate ? await this.$refs.form.validate() : false;
      if (!isValid) {
        return;
      }

      // Validate phone number
      if (this.$refs.phoneValidator) {
        const phoneValid = await this.$refs.phoneValidator.$refs.form?.validate();
        if (!phoneValid) {
          return;
        }
      }

      try {
        this.loading = true;

        if (this.isNew) {
          // For creating new staff, use the special /users endpoint
          const createPayload = {
            enabled: this.formData.enabled,
            user: {
              password: this.formData.password,
            },
            clinic: this.clinicId,
            role: 'rml.clinicStaff',
            pw: this.formData.password,
            phone: this.formData.phone, // PhoneValidator already includes country code
          };

          await api.post('users', createPayload);
          await this.updateAlertMessage({
            msg: 'Staff created successfully',
            type: 'success',
            color: 'success',
          });
        } else {
          // For updating existing staff, use the ClinicStaffProfiles endpoint
          const updatePayload = {
            clinic: this.clinicId,
            enabled: this.formData.enabled,
          };

          // Add phone if provided - PhoneValidator already includes country code
          if (this.formData.phone) {
            updatePayload.phone = this.formData.phone;
          }

          await api.patch(`rml/admin/ClinicStaffProfiles/${this.staffId}`, updatePayload);
          await this.updateAlertMessage({
            msg: 'Staff updated successfully',
            type: 'success',
            color: 'success',
          });
        }

        this.$emit('saved');
        this.$emit('close');
      } catch (error) {
        console.error('Error saving staff:', error);
        await this.updateAlertMessage({
          msg: `Failed to ${this.isNew ? 'create' : 'update'} staff : ${error.message}`,
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    async openChangePasswordDialog() {
      if (!this.staffData || !this.staffId) {
        this.updateAlertMessage({
          msg: 'Cannot get staff information',
          type: 'error',
        });
        return;
      }

      try {
        // Fetch staff data with user populated
        const staffWithUser = await crudRead(api, 'rml/admin/ClinicStaffProfiles', this.staffId, {
          populate: 'user',
        });

        if (!staffWithUser.user) {
          this.updateAlertMessage({
            msg: 'This staff member does not have a user account',
            type: 'error',
          });
          return;
        }

        // Store the populated staff data for password reset
        this.populatedStaffData = staffWithUser;

        this.changePasswordDialog = true;
        this.passwordForm = {
          newPw: null,
          confirmNewPw: null,
        };
        this.showNewPassword = false;
        this.showConfirmNewPassword = false;
      } catch (error) {
        console.error('Error fetching staff data:', error);
        this.updateAlertMessage({
          msg: 'Failed to load staff information',
          type: 'error',
        });
      }
    },

    closeChangePasswordDialog() {
      this.changePasswordDialog = false;
      this.passwordForm = {
        newPw: null,
        confirmNewPw: null,
      };
      this.showNewPassword = false;
      this.showConfirmNewPassword = false;
      this.populatedStaffData = null;
      this.$refs.passwordForm?.reset();
    },

    async resetUserPassword() {
      try {
        this.changingPassword = true;

        if (!this.populatedStaffData || !this.populatedStaffData.user) {
          throw new Error('Staff user data not available');
        }

        // Get user ID from populated staff data
        let userId;
        if (typeof this.populatedStaffData.user === 'object' && this.populatedStaffData.user._id) {
          userId = this.populatedStaffData.user._id;
        } else if (typeof this.populatedStaffData.user === 'string') {
          userId = this.populatedStaffData.user;
        } else {
          throw new Error('Invalid user ID format');
        }

        // Import the resetMasterPassword function
        const { resetMasterPassword } = await import('@/api/auth/membership');
        await resetMasterPassword(api, userId, this.passwordForm.newPw);

        await this.updateAlertMessage({
          msg: 'Password changed successfully',
          type: 'success',
          color: 'success',
        });

        this.closeChangePasswordDialog();
      } catch (error) {
        console.error('Error changing password:', error);
        await this.updateAlertMessage({
          msg: 'Failed to change password: ' + (error.message || 'Unknown error'),
          type: 'error',
        });
      } finally {
        this.changingPassword = false;
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  max-width: 900px;
  margin: 0 auto;
}
</style>
